"use client";

import { c as _c } from "react/compiler-runtime";
import type { UIMessage } from "ai";
import Image from "next/image";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckCircle } from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { isToolPart } from "@/lib/chat/messages";
import { QUICK_OFFERING_PERSISTENCE_KEY, QUICK_REQUEST_PERSISTENCE_KEY } from "@/lib/quick-flow/types";
interface ChatMessageProps {
  message: UIMessage;
}
interface ActionPrefill {
  persistenceKey: typeof QUICK_OFFERING_PERSISTENCE_KEY | typeof QUICK_REQUEST_PERSISTENCE_KEY;
  data: Record<string, unknown>;
}

/**
 * Action variants:
 *   - "primary"   → branded fill (secondary-fixed token); used for the
 *                   default forward action (e.g. "Mehr erfahren")
 *   - "secondary" → glass / outline; alternative actions
 *
 * There is no "warning" variant here any more. Its only chat emitter was the
 * OTP action, and `suggest_action` constrains variant to primary|secondary — so
 * the amber-pulse branch became unreachable when that action was removed.
 *
 * NOTHING IN THE CHAT RENDERS EMAIL VERIFICATION ANY MORE. There used to be an
 * `otp_verify_form` action type here that mounted `<ChatOtpCard>` inside the
 * message bubble. A bubble lives in a list that auto-scrolls to the bottom, so
 * every following turn pushed the one surface that captures the guest's email
 * further out of view — in prod conversation c6efd18e (2026-08-24) it was gone
 * within eight turns and the seller abandoned the flow. Its `label` was never
 * rendered at all, yet stayed in the model's context long enough for Klara to
 * quote it as an on-screen button. The confirmation now lives in the pinned
 * ChatFlowFooter, outside the scroll container. Do not reintroduce it here.
 */
type ActionVariant = "primary" | "secondary";
interface ActionItem {
  label: string;
  href?: string;
  action?: string;
  variant: ActionVariant | string;
  email?: string;
  phone?: string;
  prefill?: ActionPrefill;
}

/**
 * AI SDK v6 tool part shape.
 *
 * In v6, tool parts use:
 *   type: "tool-{toolName}" (static) or "dynamic-tool" (dynamic)
 *   state: "output-available" (completed)
 *   output: { ... }        (the tool's return value)
 *
 * Note: quick-reply chips from suggest_quick_replies are intentionally
 * NOT rendered here — they live in the bottom slot (ChatQuickReplyBar)
 * so the pill affordance has one consistent position. See
 * lib/chat/messages.ts → getLatestQuickReplies().
 */
interface ToolOutputPart {
  type: string;
  state: string;
  output?: {
    success?: boolean;
    actions?: ActionItem[];
  };
}

/**
 * Extract all user-visible text from message parts.
 *
 * All text parts are kept regardless of whether they appear alongside tool
 * calls. Intermediate conversational text ("Ich erstelle gerade...") is
 * valuable context that should remain visible to the user.
 */
export function getVisibleTextParts(parts: Array<{
  type: string;
  text?: string;
}>): string[] {
  return parts.filter(p => p.type === "text" && p.text).map(p => p.text!);
}

// ---------------------------------------------------------------------------
// Tool Activity Chip — inline status indicator for tool invocations
// ---------------------------------------------------------------------------

/** German labels for tool activity: [streaming, completed] */
const TOOL_LABELS: Record<string, [string, string]> = {
  create_offering_draft: ["Angebot wird erstellt...", "\u2713 Angebot erstellt"],
  create_request_draft: ["Anfrage wird erstellt...", "\u2713 Anfrage erstellt"],
  schedule_callback: ["R\u00fcckruf wird geplant...", "\u2713 R\u00fcckruf geplant"],
  search_offerings: ["Angebote werden gesucht...", "\u2713 Angebote gefunden"],
  get_market_stats: ["Marktdaten werden abgerufen...", "\u2713 Marktdaten abgerufen"],
  get_naturraum_info: ["Naturraum wird ermittelt...", "\u2713 Naturraum ermittelt"]
};

/** Tools that should not show a chip (pure metadata or rendered elsewhere) */
const HIDDEN_TOOLS = new Set(["suggest_action",
// → rendered as ActionButtons below the bubble
"suggest_quick_replies",
// → rendered in bottom slot (ChatQuickReplyBar)
"set_input_placeholder",
// → applied to ChatInput placeholder
"set_conversation_intent" // → silent classification metadata
]);

/** Extract the tool name from a part type like "tool-create_offering_draft" */
function extractToolName(partType: string): string | null {
  if (partType === "dynamic-tool") return null;
  if (partType.startsWith("tool-")) return partType.slice(5);
  return null;
}
function ToolActivityChip(t0) {
  const $ = _c(6);
  const {
    toolName,
    isComplete
  } = t0;
  const labels = TOOL_LABELS[toolName];
  if (!labels) {
    return null;
  }
  const label = isComplete ? labels[1] : labels[0];
  const t1 = `inline-flex items-center gap-1.5 bg-secondary-fixed/20 text-secondary font-bold text-[10px] rounded-full px-3 py-1.5 my-1 uppercase ${!isComplete ? "animate-pulse" : ""}`;
  let t2;
  if ($[0] !== isComplete) {
    t2 = isComplete && <CheckCircle className="w-3.5 h-3.5" />;
    $[0] = isComplete;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label || $[3] !== t1 || $[4] !== t2) {
    t3 = <span className={t1}>{t2}{label}</span>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * First-party hosts whose absolute URLs may render as real (new-tab) links in
 * chat. Any other off-site host is neutralised to plain text.
 */
const FIRST_PARTY_LINK_HOSTS = ["oekopunktemarkt.de", "landvest.eu"] as const;
function isFirstPartyLinkHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return FIRST_PARTY_LINK_HOSTS.some(base => host === base || host.endsWith(`.${base}`));
}

/**
 * Paths Klara may link on the LandVest host. The sister product is first-party,
 * but host-level trust was not enough: she once fabricated
 * `landvest.eu/login?partner=opm&…` by swapping the path of the successor URL
 * the prompt gave her, and this seam happily rendered the 404 as a real
 * new-tab link (prod conversation b15ea79b, 2026-07-27) — LandVest had no
 * sign-in page yet at the time.
 *
 * LandVest has since shipped a real sign-in (`/login`) and central dashboard
 * (`/dashboard`), so this list was widened to admit them (in the same commit
 * as prompt.ts's LANDVEST_ANMELDUNG — the two must move together, see
 * freeze.ts's header). Reachable paths: `/analyse` and `/analyse/<id>`
 * (getLandvestSuccessorUrl / getLandvestSuccessorAnalysisUrl), plus the exact
 * `/login` and `/dashboard` (getLandvestDashboardUrl). Anything else on the
 * host — an invented `/konto`, a hallucinated analysis UUID under some other
 * path — still degrades to plain text instead of a clickable dead end.
 * Deliberately fails safe: widen this list only alongside a real new page.
 */
function isLandvestHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === "landvest.eu" || host.endsWith(".landvest.eu");
}
function isAllowedLandvestPath(pathname: string): boolean {
  return pathname === "/analyse" || pathname.startsWith("/analyse/") || pathname === "/login" || pathname === "/dashboard";
}

/**
 * Classify an LLM-authored markdown-link href for safe rendering in the chat
 * bubble. Klara's assistant text is model-generated and steerable via user
 * input, so a link in prose is an injection/phishing surface: react-markdown's
 * urlTransform only strips `javascript:`, and a naive `startsWith("/")` check
 * mistakes protocol-relative `//evil.com` for an in-app path and hands it to
 * next/link — navigating off-site.
 *
 *   - "internal" → same-origin in-app path (one leading slash, then a path
 *                  char) → next/link. Rejects `//host` and `/\host`.
 *   - "external" → absolute http(s) URL on a first-party host → new-tab <a>.
 *   - "blocked"  → everything else (off-site host, protocol-relative,
 *                  mailto:/tel:/data:/javascript:, malformed) → plain text.
 *
 * Mirrors the CTA-button guard `isAllowedActionHref`
 * (lib/chat/tools/suggest-action) for the internal case; kept local so the
 * client bundle stays free of the chat-tool layer.
 */
export function classifyChatLink(href: string): "internal" | "external" | "blocked" {
  // Same-origin in-app path: one leading slash followed by an alphanumeric.
  if (/^\/[a-zA-Z0-9]/.test(href)) return "internal";
  try {
    const url = new URL(href);
    if ((url.protocol === "https:" || url.protocol === "http:") && isFirstPartyLinkHost(url.hostname)) {
      // LandVest is first-party but path-scoped — see isAllowedLandvestPath.
      if (isLandvestHost(url.hostname) && !isAllowedLandvestPath(url.pathname)) {
        return "blocked";
      }
      return "external";
    }
  } catch {
    // Not an absolute URL (relative / malformed / scheme-only) → blocked.
  }
  return "blocked";
}

/**
 * Custom react-markdown components for the chat bubble.
 * Renders minimal, clean markdown suitable for a chat context.
 */
const markdownComponents: Components = {
  // Paragraphs — no extra margin in compact chat bubbles
  p: ({
    children
  }) => <p className="mb-2 last:mb-0">{children}</p>,
  // Bold
  strong: ({
    children
  }) => <strong className="font-semibold">{children}</strong>,
  // Links — internal <Link>, first-party absolute <a>, everything else plain
  // text. Klara's prose is model-generated (steerable via user input), so an
  // off-site or protocol-relative href is a phishing surface — see
  // classifyChatLink.
  a: ({
    href,
    children
  }) => {
    if (!href) return <>{children}</>;
    const kind = classifyChatLink(href);
    if (kind === "internal") {
      return <Link href={href} className="underline text-secondary hover:text-secondary/80 transition-colors">
          {children}
        </Link>;
    }
    if (kind === "external") {
      return <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline text-secondary hover:text-secondary/80 transition-colors">
          {children}
        </a>;
    }
    // Blocked (off-site host, protocol-relative, javascript:/mailto:/…): drop
    // the href, keep the visible text so no content is lost.
    return <>{children}</>;
  },
  // Unordered lists
  ul: ({
    children
  }) => <ul className="list-disc list-outside ml-4 mb-2 last:mb-0 space-y-1">{children}</ul>,
  // Ordered lists
  ol: ({
    children
  }) => <ol className="list-decimal list-outside ml-4 mb-2 last:mb-0 space-y-1">{children}</ol>,
  // List items
  li: ({
    children
  }) => <li className="pl-0.5">{children}</li>,
  // Block headings — not expected in chat but rendered safely
  h1: ({
    children
  }) => <p className="font-semibold mb-1">{children}</p>,
  h2: ({
    children
  }) => <p className="font-semibold mb-1">{children}</p>,
  h3: ({
    children
  }) => <p className="font-semibold mb-1">{children}</p>,
  // Code (inline)
  code: ({
    children
  }) => <code className="bg-black/5 rounded px-1 py-0.5 text-xs font-mono">{children}</code>,
  // Block quotes
  blockquote: ({
    children
  }) => <blockquote className="border-l-2 border-zinc-300 pl-3 italic my-1">{children}</blockquote>,
  // ---- GFM tables -------------------------------------------------------
  // Klara reaches for a table whenever she reports market data (avg/min/max
  // ask + bid). Before remark-gfm was mounted those arrived as a wall of
  // literal pipes in the bubble — see the market-price reply in prod
  // conversation c6efd18e (2026-08-24).
  //
  // Content width inside the bubble is tight — measured 243 px in the 400 px
  // desktop panel (ChatPanel's sm:w-[400px]) and 222 px on a 375 px phone. The
  // 2- and 3-column tables Klara actually sends fit both; the scroll container
  // is the safety net so a wider one scrolls INSIDE the bubble rather than
  // stretching the panel.
  // `tabIndex` + `role`/`aria-label`: once a table does overflow, the scroll
  // region has to be reachable without a mouse (WCAG 2.1.1) — otherwise the
  // clipped columns are simply unavailable to keyboard users.
  table: ({
    children
  }) => <div className="overflow-x-auto my-2 last:mb-0" tabIndex={0} role="group" aria-label="Tabelle, horizontal scrollbar">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>,
  // Every row draws its own bottom rule; `<tbody>` cancels it on the final
  // row. Do NOT express this as `last:border-0` on `<tr>` — `:last-child`
  // resolves per row GROUP, so `<thead>`'s only row matches it too and the
  // header rule silently disappears (measured: borderBottomWidth 0px).
  // `divide-y` is not an option either: it sets border-WIDTH without
  // border-style, so the computed width stays 0px and nothing paints.
  tbody: ({
    children
  }) => <tbody className="[&>tr:last-child]:border-0">{children}</tbody>,
  tr: ({
    children
  }) => <tr className="border-b border-black/10">{children}</tr>,
  //
  // Column 1 is the label ("Ø Angebotspreis", "Meckl. Küstengebiet") and may
  // wrap; every later column carries a value ("1,03 € – 6,50 €") that must
  // stay on one line. That split is what lets the common 2-column stat table
  // fit the bubble without scrolling at all.
  //
  // `style` is forwarded on purpose: react-markdown puts GFM column alignment
  // (`|---:|`) there as an inline `text-align`, and an inline style outranks
  // the `text-left` default below. Destructuring only `children` drops it.
  th: ({
    children,
    style
  }) => <th style={style} className="whitespace-nowrap first:whitespace-normal px-1.5 py-1 text-left align-bottom font-semibold first:pl-0 last:pr-0">
      {children}
    </th>,
  td: ({
    children,
    style
  }) => <td style={style} className="whitespace-nowrap first:whitespace-normal px-1.5 py-1 text-left align-top first:pl-0 last:pr-0">
      {children}
    </td>
};

/**
 * Renders CTA buttons from tool results (suggest_action, create/update tools).
 *
 * Supports two button types:
 * - Navigation buttons (href) → rendered as Next.js <Link>
 * - Client-side actions (action) → rendered as <button> with onClick handler
 *   Currently supported: "open-login-modal" (opens the registration/login modal)
 */
/**
 * Write funnel prefill to localStorage using the same shape as the
 * /lp/* landing pages (`{ data, timestamp }`). The funnel's
 * useFormPersistence reads this key on mount and hydrates the form.
 * Silent failure is fine — it's only prefill, users can always re-enter.
 */
function writeFunnelPrefill(prefill: ActionPrefill): void {
  try {
    const persisted = {
      data: prefill.data,
      timestamp: Date.now()
    };
    localStorage.setItem(prefill.persistenceKey, JSON.stringify(persisted));
  } catch {
    // ignore — prefill is nice-to-have
  }
}
function variantClasses(variant: ActionItem["variant"]): string {
  if (variant === "primary") {
    return "bg-secondary-fixed text-on-secondary-fixed shadow-lg shadow-secondary-fixed/20";
  }
  return "glass-light border border-surface-container text-primary hover:bg-white/80";
}
function ActionButtons(t0) {
  const $ = _c(7);
  const {
    actions
  } = t0;
  const {
    openLoginModal
  } = useAuth();
  let t1;
  if ($[0] !== actions || $[1] !== openLoginModal) {
    let t2;
    if ($[3] !== openLoginModal) {
      t2 = (action, i) => {
        const className = `
          inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5
          text-sm font-extrabold transition-all text-center no-underline cursor-pointer active:scale-95
          ${variantClasses(action.variant)}
        `;
        const label = action.label;
        if (action.action === "open-login-modal") {
          return <button key={i} onClick={() => openLoginModal({
            email: action.email,
            phone: action.phone
          })} className={className}>{label}</button>;
        }
        if (!action.href) {
          return null;
        }
        const prefill = action.prefill;
        return <Link key={i} href={action.href} onClick={prefill ? () => writeFunnelPrefill(prefill) : undefined} className={className}>{label}</Link>;
      };
      $[3] = openLoginModal;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = actions.map(t2);
    $[0] = actions;
    $[1] = openLoginModal;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-col gap-2 mt-3">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Renders a single chat message bubble.
 *
 * Parts are rendered in conversation order: text as markdown, tool
 * invocations as compact activity chips, and action buttons at the bottom.
 */
export function ChatMessage(t0) {
  const $ = _c(17);
  const {
    message
  } = t0;
  const isUser = message.role === "user";
  if (message.role === "system") {
    return null;
  }
  let contentElements;
  let dedupedActions;
  let hasVisibleContent;
  if ($[0] !== isUser || $[1] !== message.parts) {
    contentElements = [];
    const seenKeys = new Set();
    dedupedActions = [];
    hasVisibleContent = false;
    for (const part of message.parts) {
      if (part.type === "text" && (part as {
        text?: string;
      }).text) {
        hasVisibleContent = true;
        const text = (part as {
          text: string;
        }).text;
        contentElements.push(isUser ? <span key={contentElements.length}>{text}</span> : <ReactMarkdown key={contentElements.length} remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>);
      } else {
        if (isToolPart(part)) {
          const toolPart = part as unknown as ToolOutputPart;
          if (toolPart.state === "output-available" && toolPart.output?.success && Array.isArray(toolPart.output?.actions)) {
            for (const action of toolPart.output.actions) {
              const key = action.action ?? action.href ?? action.label;
              if (!seenKeys.has(key)) {
                seenKeys.add(key);
                dedupedActions.push(action);
              }
            }
          }
          const toolName = extractToolName(part.type);
          const failed = toolPart.state === "output-error" || toolPart.state === "output-available" && toolPart.output?.success === false;
          if (toolName && !HIDDEN_TOOLS.has(toolName) && TOOL_LABELS[toolName] && !failed) {
            hasVisibleContent = true;
            const isComplete = toolPart.state === "output-available";
            contentElements.push(<ToolActivityChip key={contentElements.length} toolName={toolName} isComplete={isComplete} />);
          }
        }
      }
    }
    $[0] = isUser;
    $[1] = message.parts;
    $[2] = contentElements;
    $[3] = dedupedActions;
    $[4] = hasVisibleContent;
  } else {
    contentElements = $[2];
    dedupedActions = $[3];
    hasVisibleContent = $[4];
  }
  if (message.role === "assistant" && !hasVisibleContent && dedupedActions.length === 0) {
    return null;
  }
  const t1 = `flex ${isUser ? "justify-end" : "justify-start"} mb-3`;
  let t2;
  if ($[5] !== isUser) {
    t2 = !isUser && <div className="shrink-0 mr-2 mt-1"><Image src="/images/chat/klara.jpeg" alt="" width={28} height={28} className="h-7 w-7 rounded-[9999px] object-cover" /></div>;
    $[5] = isUser;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = `
          rounded-2xl px-4 py-2.5 text-sm leading-relaxed
          ${isUser ? "max-w-[85%] bg-primary-container text-white rounded-tr-none" : "max-w-[calc(85%-36px)] glass-light text-primary rounded-tl-none"}
        `;
  let t4;
  if ($[7] !== dedupedActions) {
    t4 = dedupedActions.length > 0 && <ActionButtons actions={dedupedActions} />;
    $[7] = dedupedActions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== contentElements || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t3}>{contentElements}{t4}</div>;
    $[9] = contentElements;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t5) {
    t6 = <div className={t1}>{t2}{t5}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}